import * as WalletModel from 'models/wallet';
import * as WalletRepository from 'repositories/wallet';

import { push } from 'react-router-redux';
import { Dispatch } from 'redux';

import { FlashMessageActionCreators } from 'actions/flashMessage';
import { LayoutActionCreators } from 'actions/layout';
import { ROUTE_WALLET_DASHBOARD, ROUTE_WALLET_REDEEM_FINISH, ROUTE_WALLET_TOPUP_ERROR, ROUTE_WALLET_TRANSFER_FINISH } from 'constants/app';
import { BALANCE_OVER_LIMIT, INVALID_AMOUNT, MONEY_IN_PER_MONTH_OVER_LIMIT } from 'constants/error';
import { app } from 'helper/app';
import { checkout } from 'helper/checkout';
import { handleGenericApiError } from 'helper/error';
import { formatCurrency } from 'helper/form';
import { ActionCreator } from 'helper/redux';
import { ErrorResponse } from 'models/error';
import { Thunk } from 'models/redux';

export const WalletActionCreators = {
  appendHistory: new ActionCreator<'wallet/AppendHistory', WalletModel.WalletTransactionList>('wallet/AppendHistory'),
  clearHistory: new ActionCreator<'wallet/ClearHistory', void>('wallet/ClearHistory'),
  clearRedeem: new ActionCreator<'wallet/ClearRedeem', void>('wallet/ClearRedeem'),
  clearTransfer: new ActionCreator<'wallet/ClearTransfer', void>('wallet/ClearTransfer'),
  setAccountIsOnRequest: new ActionCreator<'wallet/SetAccountIsOnRequest', void>('wallet/SetAccountIsOnRequest'),
  setAccountIsReceived: new ActionCreator<'wallet/SetAccountIsReceived', void>('wallet/SetAccountIsReceived'),
  setBankListIsOnRequest: new ActionCreator<'wallet/SetBankListIsOnRequest', void>('wallet/SetBankListIsOnRequest'),
  setBankListIsReceived: new ActionCreator<'wallet/SetBankListIsReceived', void>('wallet/SetBankListIsReceived'),
  setHistoryIsOnRequest: new ActionCreator<'wallet/SetHistoryIsOnRequest', void>('wallet/SetHistoryIsOnRequest'),
  setHistoryIsReceived: new ActionCreator<'wallet/SetHistoryIsReceived', void>('wallet/SetHistoryIsReceived'),
  setRedeemAccountListIsOnRequest: new ActionCreator<'wallet/SetRedeemAccountListIsOnRequest', void>('wallet/SetRedeemAccountListIsOnRequest'),
  setRedeemAccountListIsReceived: new ActionCreator<'wallet/SetRedeemAccountListIsReceived', void>('wallet/SetRedeemAccountListIsReceived'),
  setTransactionIsOnRequest: new ActionCreator<'wallet/SetTransactionIsOnRequest', void>('wallet/SetTransactionIsOnRequest'),
  setTransactionIsReceived: new ActionCreator<'wallet/SetTransactionIsReceived', void>('wallet/SetTransactionIsReceived'),
  updateAccount: new ActionCreator<'wallet/UpdateAccount', WalletModel.WalletAccount>('wallet/UpdateAccount'),
  updateBankList: new ActionCreator<'wallet/UpdateBankList', WalletModel.WalletBankList>('wallet/UpdateBankList'),
  updateMoneyInPerMonth: new ActionCreator<'wallet/UpdateMoneyInPerMonth', number>('wallet/UpdateMoneyInPerMonth'),
  updateRedeem: new ActionCreator<'wallet/UpdateRedeem', WalletModel.WalletRedeem>('wallet/UpdateRedeem'),
  updateRedeemAccount: new ActionCreator<'wallet/UpdateRedeemAccount', WalletModel.WalletRedeemAccount>('wallet/UpdateRedeemAccount'),
  updateRedeemAccountList: new ActionCreator<'wallet/UpdateRedeemAccountList', WalletModel.WalletRedeemAccountList>('wallet/UpdateRedeemAccountList'),
  updateRedeemAmount: new ActionCreator<'wallet/UpdateRedeemAmount', number>('wallet/UpdateRedeemAmount'),
  updateTopUpError: new ActionCreator<'wallet/UpdateTopUpError', string>('wallet/UpdateTopUpError'),
  updateTransactionListFilter: new ActionCreator<'wallet/UpdateTransactionListFilter', WalletModel.WalletTransactionListFilter>('wallet/UpdateTransactionListFilter'),
  updateTransactionSummary: new ActionCreator<'wallet/UpdateTransactionSummary', WalletModel.WalletTransactionSummary>('wallet/UpdateTransactionSummary'),
  updateTransferAmount: new ActionCreator<'wallet/UpdateTransferAmount', number>('wallet/UpdateTransferAmount'),
  updateTransferError: new ActionCreator<'wallet/UpdateTransferError', string>('wallet/UpdateTransferError'),
  updateTransferPhone: new ActionCreator<'wallet/UpdateTransferPhone', string>('wallet/UpdateTransferPhone')
};

export function loadMoreHistory(transactionListFilter: WalletModel.WalletTransactionListFilter): Thunk {
  return (dispatch, getState) => {
    dispatch(WalletActionCreators.setHistoryIsOnRequest.create());
    return WalletRepository.getTransactionList(transactionListFilter)
      .then((transaction) => {
        dispatch(WalletActionCreators.appendHistory.create(transaction));
        const currentHistoryContent = getState().walletState.historyState.data.content;
        const currentFilter = getState().walletState.historyState.filter;
        const lastCurrentHistoryContent = currentHistoryContent[currentHistoryContent.length - 1];
        if (lastCurrentHistoryContent) {
          const newCreatedAtEnd = lastCurrentHistoryContent.createdAt;
          dispatch(WalletActionCreators.updateTransactionListFilter
            .create({ ...currentFilter, createdAtEnd: newCreatedAtEnd }));
        }
        dispatch(WalletActionCreators.setHistoryIsReceived.create());
      })
      .catch((e) => {
        dispatch(WalletActionCreators.setHistoryIsReceived.create());
        handleGenericApiError('action.failed.wallet.load.more.history', e, dispatch);
        console.error(e); // tslint:disable-line
      });
  };
}

export function getWalletAccount(): Thunk {
  return (dispatch) => {
    dispatch(WalletActionCreators.setAccountIsOnRequest.create());

    WalletRepository.getWalletAccount()
      .then((walletAccount) => {
        dispatch(WalletActionCreators.setAccountIsReceived.create());
        dispatch(WalletActionCreators.updateAccount.create(walletAccount));
      })
      .catch((e) => {
        dispatch(WalletActionCreators.setAccountIsReceived.create());
        handleGenericApiError('action.failed.wallet.get.account', e, dispatch);
        console.error(e); // tslint:disable-line
      });
  };
}

export function getTransactionSummary(): Thunk {
  return (dispatch) => {
    dispatch(WalletActionCreators.setTransactionIsOnRequest.create());

    WalletRepository.getTransactionSummary()
      .then((updatedTransactionSummary) => {
        dispatch(WalletActionCreators.setTransactionIsReceived.create());
        dispatch(WalletActionCreators.updateTransactionSummary.create(updatedTransactionSummary));
      })
      .catch((e) => {
        dispatch(WalletActionCreators.setTransactionIsReceived.create());
        handleGenericApiError('action.failed.wallet.get.transactionSummary', e, dispatch);
        console.error(e); // tslint:disable-line
      });
  };
}

export function topUp(recipientName: string, formValue: WalletModel.WalletTopUpForm): Thunk {
  return (dispatch) => {
    dispatch(LayoutActionCreators.showLoading.create());

    validateTopUp(formValue.amount, dispatch)
      .then(() => {
        WalletRepository.topUp(formValue)
          .then((response) => {
            const landingUrl = app.generateUrl(ROUTE_WALLET_DASHBOARD);
            checkout.open(response.referenceNo, {
              landingUrl,
              name: recipientName
            });
          })
          .catch((e: ErrorResponse) => {
            if (e && e.body && e.body.error === INVALID_AMOUNT && e.body.errorParams) {
              const { maxAmount, minAmount } = e.body.errorParams;
              dispatch(FlashMessageActionCreators.show.create({
                modifier: 'danger',
                param: {
                  maxAmount: formatCurrency(maxAmount),
                  minAmount: formatCurrency(minAmount)
                },
                text: 'action.failed.wallet.topUp.invalidAmount'
              }));
            } else {
              handleGenericApiError('action.failed.wallet.topUp', e, dispatch);
            }
            dispatch(LayoutActionCreators.hideLoading.create());
            console.error(e); // tslint:disable-line
          });
      })
      .catch((errorResponse: ErrorResponse) => {
        dispatch(WalletActionCreators.updateTopUpError.create(errorResponse.body.error));
        dispatch(push(ROUTE_WALLET_TOPUP_ERROR));
        dispatch(LayoutActionCreators.hideLoading.create());
      });
  };
}

export function getRedeemAccountList(walletRedeemAccountListFilter: WalletModel.WalletRedeemAccountListFilter): Thunk {
  return (dispatch) => {
    dispatch(WalletActionCreators.setRedeemAccountListIsOnRequest.create());
    dispatch(LayoutActionCreators.showLoading.create());

    WalletRepository.getRedeemAccountList(walletRedeemAccountListFilter)
      .then((response) => {
        dispatch(WalletActionCreators.updateRedeemAccountList.create(response));
        dispatch(WalletActionCreators.setRedeemAccountListIsReceived.create());
        dispatch(LayoutActionCreators.hideLoading.create());
      })
      .catch((e) => {
        handleGenericApiError('action.failed.wallet.redeem.history', e, dispatch);
        dispatch(WalletActionCreators.setRedeemAccountListIsReceived.create());
        dispatch(LayoutActionCreators.hideLoading.create());
        console.error(e); // tslint:disable-line
      });
  };
}

export function redeem(formValue: WalletModel.WalletRedeemOtpForm): Thunk {
  return (dispatch) => {
    dispatch(LayoutActionCreators.showLoading.create());

    WalletRepository.redeem(formValue)
      .then((response) => {
        dispatch(WalletActionCreators.updateRedeem.create(response));
        dispatch(push(ROUTE_WALLET_REDEEM_FINISH));
        dispatch(LayoutActionCreators.hideLoading.create());
      })
      .catch((e) => {
        handleGenericApiError('action.failed.wallet.redeem', e, dispatch);
        dispatch(LayoutActionCreators.hideLoading.create());
        console.error(e); // tslint:disable-line
      });
  };
}

export function getBankList(walletBankListFilter: WalletModel.WalletBankListFilter): Thunk {
  return (dispatch) => {
    dispatch(WalletActionCreators.setBankListIsOnRequest.create());

    WalletRepository.getBankList(walletBankListFilter)
      .then((bankList) => {
        dispatch(WalletActionCreators.updateBankList.create(bankList));
        dispatch(WalletActionCreators.setBankListIsReceived.create());
      })
      .catch((e) => {
        console.error(e); // tslint:disable-line
      });
  };
}

export function transfer(formValue: WalletModel.WalletTransferOtpForm): Thunk {
  return (dispatch) => {
    dispatch(LayoutActionCreators.showLoading.create());

    WalletRepository.transfer(formValue)
      .then((response) => {
        dispatch(push(ROUTE_WALLET_TRANSFER_FINISH));
        dispatch(LayoutActionCreators.hideLoading.create());
      })
      .catch((e) => {
        if (e.body.error === BALANCE_OVER_LIMIT || e.body.error === MONEY_IN_PER_MONTH_OVER_LIMIT) {
          dispatch(WalletActionCreators.updateTransferError.create(e.body.error));
          dispatch(push(ROUTE_WALLET_TRANSFER_FINISH));
        } else {
          handleGenericApiError('action.failed.wallet.transfer', e, dispatch);
        }
        dispatch(LayoutActionCreators.hideLoading.create());
        console.error(e); // tslint:disable-line
      });
  };
}

function validateTopUp(amount: number, dispatch: Dispatch<void>) {
  return new Promise((resolve, reject) => {
    WalletRepository.getWalletAccount()
      .then((walletAccount) => {
        if (walletAccount.balance + amount > walletAccount.type.balanceLimit) {
          throw ({ body: { error: BALANCE_OVER_LIMIT } });
        }

        return walletAccount;
      })
      .then((walletAccount) => {
        return new Promise((valid, invalid) => {
          WalletRepository.getTransactionSummary()
            .then((summary) => {
              dispatch(WalletActionCreators.updateMoneyInPerMonth.create(summary.moneyInPerMonth));
              if (summary.moneyInPerMonth + amount > walletAccount.type.moneyInLimitPerMonth) {
                invalid({ body: { error: MONEY_IN_PER_MONTH_OVER_LIMIT } });
              }

              valid();
            });
        });
      })
      .then(() => {
        resolve();
      })
      .catch((errorResponse: ErrorResponse) => {
        reject(errorResponse);
      });
  });
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/wallet.ts